/**
 * ContractorOnboardingModal (P5)
 *
 * 4-step dismissible modal. Both "Skip for now" and "Got it" complete
 * the onboarding flag (markContractorOnboardingComplete). Reusable for
 * the persistent "How Koredor Works" link via the `singlePage` prop —
 * which renders all steps without requiring completion.
 *
 * Pure presentation. No new mutations beyond the single completion flag.
 * Copy comes exclusively from contractorMessaging.ONBOARDING_STEPS /
 * ONBOARDING_MODAL_COPY — no inline literals.
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import {
  ONBOARDING_STEPS,
  ONBOARDING_MODAL_COPY,
  EXPECTATION_FRAMING,
} from "@/data/contractor/contractorMessaging";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called for both Skip and Got it. Single canonical completion path. */
  onComplete: () => void;
  /** When true (persistent link mode), all steps are listed at once and
   *  there's no completion side-effect — purely informational re-read. */
  singlePage?: boolean;
}

export function ContractorOnboardingModal({
  open,
  onOpenChange,
  onComplete,
  singlePage = false,
}: Props) {
  const [stepIdx, setStepIdx] = useState(0);
  const last = ONBOARDING_STEPS.length - 1;
  const step = ONBOARDING_STEPS[stepIdx];

  const handleClose = (next: boolean) => {
    if (!next && !singlePage) onComplete();
    onOpenChange(next);
  };

  const handleSkip = () => {
    onComplete();
    onOpenChange(false);
  };

  const handleFinish = () => {
    onComplete();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="contractor-onboarding-modal"
        className="max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{ONBOARDING_MODAL_COPY.dialogTitle}</DialogTitle>
        </DialogHeader>

        {singlePage ? (
          <div className="space-y-4 py-2">
            {ONBOARDING_STEPS.map((s) => (
              <section key={s.key} data-step={s.key}>
                <p className="text-sm font-bold text-foreground mb-1">
                  {s.title}
                </p>
                <p className="text-[12px] text-muted-foreground leading-relaxed">
                  {s.body}
                </p>
              </section>
            ))}
            <div className="rounded-md bg-muted/30 border border-border/50 px-3 py-2">
              <p className="text-[11px] text-muted-foreground">
                {EXPECTATION_FRAMING.curated} {EXPECTATION_FRAMING.matching}
              </p>
            </div>
          </div>
        ) : (
          <div className="py-2">
            {/* Step indicator */}
            <div
              className="flex items-center gap-1.5 mb-3"
              data-testid="onboarding-step-indicator"
            >
              {ONBOARDING_STEPS.map((s, i) => (
                <span
                  key={s.key}
                  className={`h-1.5 flex-1 rounded-full transition ${
                    i <= stepIdx ? "bg-primary" : "bg-muted"
                  }`}
                />
              ))}
            </div>
            <section data-step={step.key} data-testid="onboarding-current-step">
              <p className="text-sm font-bold text-foreground mb-1.5">
                {step.title}
              </p>
              <p className="text-[12px] text-muted-foreground leading-relaxed">
                {step.body}
              </p>
            </section>
            {stepIdx === last && (
              <div className="mt-3 rounded-md bg-primary/5 border border-primary/20 px-3 py-2 flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <p className="text-[11px] text-foreground/90">
                  {ONBOARDING_MODAL_COPY.cta}
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
          {!singlePage && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              data-testid="onboarding-skip"
            >
              {ONBOARDING_MODAL_COPY.skip}
            </Button>
          )}
          <div className="flex items-center gap-2 ml-auto">
            {!singlePage && stepIdx > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
              >
                <ChevronLeft className="h-3 w-3 mr-1" />
                {ONBOARDING_MODAL_COPY.back}
              </Button>
            )}
            {!singlePage && stepIdx < last && (
              <Button
                type="button"
                size="sm"
                onClick={() => setStepIdx((i) => Math.min(last, i + 1))}
                data-testid="onboarding-next"
              >
                {ONBOARDING_MODAL_COPY.next}
                <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            )}
            {(singlePage || stepIdx === last) && (
              <Button
                type="button"
                size="sm"
                onClick={singlePage ? () => onOpenChange(false) : handleFinish}
                data-testid="onboarding-finish"
              >
                {ONBOARDING_MODAL_COPY.finish}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
